/* Assemble optimized JPEGs into one PDF, in the browser.
 *
 * WHY HERE AND NOT ON THE SERVER. The bytes are already in the browser: the
 * batch fetched every optimized image from the CDN to build the zip, and they
 * are sitting in object URLs. A PDF holding JPEGs is a container and nothing
 * more -- the image data goes in untouched, as a /DCTDecode stream -- so there
 * is no compression to do and no reason to send several megabytes up to a
 * server and the same megabytes back down. Doing it here is instant, adds no
 * endpoint to defend, and means the assembled document never leaves the
 * device at all, which fits a tool people hand personal photographs to.
 *
 * WHY NOT A LIBRARY. jsPDF is about 110 KB gzipped, and would be carried by
 * every visitor for a button most never press. The subset of PDF needed for
 * "one JPEG per page" is small and completely specified, so it is written out
 * here instead. What it does NOT do is anything else: no text, no vectors, no
 * compression, no encryption.
 *
 * JPEG ONLY, and that is a real limit rather than an oversight. A JPEG embeds
 * into a PDF byte-for-byte because /DCTDecode is the same encoding. PNG would
 * need /FlateDecode with predictors, and WebP and AVIF have no PDF filter at
 * all -- they would have to be decoded and re-encoded, which means quality
 * loss on the way into a file whose whole point is to be the same pictures.
 * The caller checks the format and hides the button rather than surprising
 * anyone. See downloadBatchPdf() in compositions/utils.js.
 */

// The rasterizer renders PDF pages at 150 dpi (see pdf_pages.py), so mapping
// back at 150 returns a page to the size it started as: drop a PDF in, take
// the pages out, put them back, and the paper is the same size it was.
export const DPI = 150

// A PDF unit is 1/72 inch and no page may exceed 14400 of them -- 200 inches,
// the format's own ceiling. A 4000-pixel photo at 150 dpi is only 19 inches,
// so this is a guard rather than a routine path.
const MAX_POINTS = 14400

// Markers that carry a frame header. C4 is a Huffman table, C8 is reserved and
// CC is arithmetic coding -- all three sit in this range and none of them
// describes the image, which is the usual bug in a hand-written SOF scanner.
const SOF_MARKERS = new Set([
    0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
    0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
])

/* Pixel dimensions and component count, read from the JPEG itself.
 *
 * Read from the bytes rather than from an <img>, for two reasons: it needs no
 * decode (a 50-page document would otherwise decode 50 images just to be
 * measured), and the component count is not something an <img> will tell you,
 * yet it decides the PDF colour space. Returns null for anything that is not a
 * JPEG this can describe. */
export function jpegInfo(bytes) {
    if (!bytes || bytes.length < 4) return null
    if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null   // not SOI

    let i = 2
    while (i + 3 < bytes.length) {
        if (bytes[i] !== 0xff) {
            // Fill bytes are legal between segments; anything else means the
            // stream is not what it claims and guessing would be worse.
            i++
            continue
        }
        const marker = bytes[i + 1]

        // Standalone markers carry no length: padding, restarts, SOI, EOI.
        if (marker === 0xff || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
            i += 2
            continue
        }

        const length = (bytes[i + 2] << 8) | bytes[i + 3]
        if (length < 2) return null

        if (SOF_MARKERS.has(marker)) {
            // [precision][height hi][height lo][width hi][width lo][components]
            if (i + 9 >= bytes.length) return null
            const height = (bytes[i + 5] << 8) | bytes[i + 6]
            const width = (bytes[i + 7] << 8) | bytes[i + 8]
            const components = bytes[i + 9]
            if (!width || !height) return null
            return {width, height, components}
        }

        // Start of scan: the frame header is behind us, so if it were coming it
        // would have arrived.
        if (marker === 0xda) return null

        i += 2 + length
    }
    return null
}

/* The PDF colour space for a component count. Anything else is refused rather
 * than guessed: a wrong colour space renders as a picture in the wrong colours,
 * which is worse than an honest refusal. */
function colourSpace(components) {
    if (components === 1) return "/DeviceGray"
    if (components === 3) return "/DeviceRGB"
    if (components === 4) return "/DeviceCMYK"
    return null
}

/* Page size in PDF points for an image, clamped to what the format allows.
 * Both edges scale together, so a clamped page keeps the picture's shape. */
export function pageSize(width, height, dpi = DPI) {
    let w = (width / dpi) * 72
    let h = (height / dpi) * 72
    const worst = Math.max(w, h)
    if (worst > MAX_POINTS) {
        const scale = MAX_POINTS / worst
        w *= scale
        h *= scale
    }
    return {w: round(w), h: round(h)}
}

// PDF accepts real numbers; three places is far finer than a printer resolves
// and keeps the file free of 17-digit floats.
function round(n) {
    return Math.round(n * 1000) / 1000
}

const ASCII = (s) => {
    const out = new Uint8Array(s.length)
    for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff
    return out
}

/* Build the document.
 *
 * `images` is [{bytes, name}] in the order the pages should appear. Anything
 * that is not a JPEG this can describe is SKIPPED rather than failing the whole
 * document -- one odd file among fifty should not cost the other forty-nine --
 * and the count of what went in is returned so the caller can say so.
 *
 * Returns {bytes, pages, skipped}. bytes is null when nothing usable was left. */
export function buildPdf(images, opts = {}) {
    const dpi = opts.dpi || DPI
    const usable = []
    let skipped = 0

    for (const img of images || []) {
        const info = img && img.bytes ? jpegInfo(img.bytes) : null
        const space = info ? colourSpace(info.components) : null
        if (!info || !space) {
            skipped++
            continue
        }
        usable.push({bytes: img.bytes, info, space})
    }

    if (!usable.length) return {bytes: null, pages: 0, skipped}

    // Objects: 1 catalog, 2 pages, then three per page -- the page, its content
    // stream, and the image itself.
    const chunks = []
    const offsets = []          // byte offset of each object, indexed from 1
    let length = 0

    const put = (data) => {
        const bytes = typeof data === "string" ? ASCII(data) : data
        chunks.push(bytes)
        length += bytes.length
    }
    const startObject = (n) => {
        offsets[n] = length
        put(`${n} 0 obj\n`)
    }

    put("%PDF-1.4\n")
    // A comment of high bytes, so anything that transfers this file decides it
    // is binary rather than mangling the line endings of the JPEG inside it.
    put(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]))

    const pageObjectNumber = (i) => 3 + i * 3
    const total = usable.length

    startObject(1)
    put("<< /Type /Catalog /Pages 2 0 R >>\nendobj\n")

    startObject(2)
    const kids = usable.map((_, i) => `${pageObjectNumber(i)} 0 R`).join(" ")
    put(`<< /Type /Pages /Count ${total} /Kids [${kids}] >>\nendobj\n`)

    usable.forEach((page, i) => {
        const pageNo = pageObjectNumber(i)
        const contentNo = pageNo + 1
        const imageNo = pageNo + 2
        const {w, h} = pageSize(page.info.width, page.info.height, dpi)

        startObject(pageNo)
        put(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] ` +
            `/Resources << /XObject << /Im0 ${imageNo} 0 R >> >> ` +
            `/Contents ${contentNo} 0 R >>\nendobj\n`)

        // The image is drawn filling the page exactly: the page was sized from
        // the image, so there is never a margin and never a letterbox.
        const content = `q\n${w} 0 0 ${h} 0 0 cm\n/Im0 Do\nQ\n`
        startObject(contentNo)
        put(`<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`)

        startObject(imageNo)
        put(`<< /Type /XObject /Subtype /Image /Width ${page.info.width} ` +
            `/Height ${page.info.height} /ColorSpace ${page.space} ` +
            `/BitsPerComponent 8 /Filter /DCTDecode ` +
            `/Length ${page.bytes.length} >>\nstream\n`)
        put(page.bytes)              // byte for byte; this is the whole trick
        put("\nendstream\nendobj\n")
    })

    // The cross-reference table. Entries are exactly twenty bytes each and the
    // reader trusts that arithmetic, so the padding is not cosmetic.
    const objectCount = 2 + total * 3
    const xrefOffset = length
    put(`xref\n0 ${objectCount + 1}\n`)
    put("0000000000 65535 f \n")
    for (let n = 1; n <= objectCount; n++) {
        put(`${String(offsets[n]).padStart(10, "0")} 00000 n \n`)
    }
    put(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\n`)
    put(`startxref\n${xrefOffset}\n%%EOF\n`)

    const bytes = new Uint8Array(length)
    let at = 0
    for (const c of chunks) {
        bytes.set(c, at)
        at += c.length
    }
    return {bytes, pages: total, skipped}
}

/* A name for the assembled file, taken from the pages it was made of.
 *
 * The pages a PDF produced are called <stem>_01.jpg ... <stem>_50.jpg, so
 * stripping that suffix returns the name of the document they came out of --
 * which is what somebody putting them back together expects to see. */
export function assembledName(firstPageName, fallback = "images") {
    const noExt = String(firstPageName || "").replace(/\.[^.]+$/, "")
    const stem = noExt.replace(/[_-]\d{1,4}$/, "").trim()
    return `${stem || fallback}.pdf`
}
